import React from "react";
import { Helmet } from "react-helmet-async";
import { SEOProps } from "./types";
import { defaultSEOConfig } from "../../config/seo.config";
import {
  formatTitle,
  formatKeywords,
  getCanonicalUrl,
  generateBreadcrumbSchema,
} from "../../utils/seo.utils";

/**
 * SEO Component
 * Manages all SEO-related meta tags, Open Graph, Twitter Cards,
 * and structured data for the application
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  author,
  canonical,
  lang = "ja",
  locale,
  openGraph,
  twitter,
  structuredData,
  breadcrumbs,
  noindex = false,
  nofollow = false,
  additionalMetaTags = [],
  additionalLinkTags = [],
}) => {
  // Format title
  const pageTitle = title
    ? formatTitle(
        title,
        defaultSEOConfig.titleTemplate,
        defaultSEOConfig.defaultTitle
      )
    : defaultSEOConfig.defaultTitle;

  // Use provided description or default
  const pageDescription = description || defaultSEOConfig.defaultDescription;

  // Format keywords
  const pageKeywords = keywords
    ? formatKeywords([...defaultSEOConfig.defaultKeywords, ...keywords])
    : formatKeywords(defaultSEOConfig.defaultKeywords);

  // Determine canonical URL
  const canonicalUrl = canonical
    ? getCanonicalUrl(canonical)
    : typeof window !== "undefined"
      ? window.location.href
      : defaultSEOConfig.siteUrl;

  // Locale settings
  const pageLocale = locale || defaultSEOConfig.defaultLocale;

  // Open Graph defaults
  const ogTitle = openGraph?.title || title || defaultSEOConfig.defaultTitle;
  const ogDescription = openGraph?.description || pageDescription;
  const ogImage = openGraph?.image
    ? openGraph.image.startsWith("http")
      ? openGraph.image
      : `${defaultSEOConfig.siteUrl}${openGraph.image}`
    : `${defaultSEOConfig.siteUrl}${defaultSEOConfig.defaultImage}`;
  const ogUrl = openGraph?.url || canonicalUrl;
  const ogType = openGraph?.type || "website";
  const ogSiteName = openGraph?.siteName || "就活ダッシュボード";
  const ogLocale = openGraph?.locale || pageLocale;

  // Twitter Card defaults
  const twitterCard = twitter?.card || "summary_large_image";
  const twitterTitle = twitter?.title || ogTitle;
  const twitterDescription = twitter?.description || ogDescription;
  const twitterImage = twitter?.image
    ? twitter.image.startsWith("http")
      ? twitter.image
      : `${defaultSEOConfig.siteUrl}${twitter.image}`
    : ogImage;
  const twitterSite = twitter?.site || defaultSEOConfig.twitterHandle;

  // Robots meta
  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
  ].join(", ");

  // Prepare structured data
  const allStructuredData: any[] = [];

  // Add breadcrumbs if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    allStructuredData.push(generateBreadcrumbSchema(breadcrumbs));
  }

  // Add custom structured data
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      allStructuredData.push(...structuredData);
    } else {
      allStructuredData.push(structuredData);
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      {author && <meta name="author" content={author} />}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content={ogSiteName} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitter?.creator && (
        <meta name="twitter:creator" content={twitter.creator} />
      )}

      {/* Additional Meta Tags */}
      {additionalMetaTags.map((tag, index) => {
        if (tag.name) {
          return (
            <meta key={`meta-${index}`} name={tag.name} content={tag.content} />
          );
        }
        if (tag.property) {
          return (
            <meta
              key={`meta-${index}`}
              property={tag.property}
              content={tag.content}
            />
          );
        }
        return null;
      })}

      {/* Additional Link Tags */}
      {additionalLinkTags.map((tag, index) => (
        <link key={`link-${index}`} {...tag} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {allStructuredData.map((data, index) => (
        <script key={`structured-${index}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
